import { batch } from "solid-js";
import { reconcile, SetStoreFunction } from "solid-js/store";

import { AddressUsa } from "@repo/kikoff-models/Address";
import { FullNameCanada, FullNameUsa } from "@repo/kikoff-models/FullName";
import User from "@repo/kikoff-models/User";
import t from "@repo/localization/string.macro";
import canadaRpc from "@repo/protobuf/api/canadaRpc";
import usRpc from "@repo/protobuf/api/usRpc";
import { OnboardingState as OnboardingStatePb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/onboarding_state_pb";
import type {
  UpdateAddressRequest,
  UpdateDOBRequest,
  UpdateNameRequest,
  UpdatePhoneRequest,
  UpdateSSNRequest,
} from "@repo/protobuf/gen/web/public/onboarding_pb";
import { OnboardingStatus as OnboardingStatusPb } from "@repo/protobuf/gen/web/public/onboarding_pb";
import { retryStatuses } from "@repo/protobuf/usa/factories/models/createOnboardingStatus";
import Protobuf, { MessageFrom } from "@repo/protobuf/utils";
import Address from "@repo/utils/Address";
import FullName from "@repo/utils/FullName";
import UArray from "@repo/utils/UArray";
import UDate from "@repo/utils/UDate";
import UError from "@repo/utils/UError";
import UFunction from "@repo/utils/UFunction";
import UPromise from "@repo/utils/UPromise";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import { useExperiments } from "./experiments";
import { useUser } from "./user";

export namespace Onboarding {
  export type Stage =
    | { name: "confirm_email" }
    | {
        name: "pii_entry";
        retryReason?: "default" | "address";
        stepByName: Onboarding.State.StepByName;
        stepOrder: Onboarding.Step.Name[];
        phoneConfirmationToken?: string;
      }
    | { name: "pending_idv" }
    | { name: "not_eligible"; reason: "underaged" | "address_subdivision" }
    | { name: "manual_idv"; photoId?: File; selfie?: File }
    | { name: "pending_manual_idv_review" }
    | { name: "failed_manual_idv" }
    | { name: "manual_idv_needs_reupload" }
    | { name: "persona_manual_upload" }
    | { name: "ecbsv_consent" }
    | { name: "pending_itin_review" }
    | { name: "duplicate_account" }
    | { name: "credit_freeze" }
    | { name: "fraud_alert" }
    | { name: "flagged" }
    | { name: "passed" };

  export const routesByStage: Partial<
    Record<Stage["name"], string | string[]>
  > = {
    confirm_email: "/signup/verify-email",
    pii_entry: ["/signup/intro", "/signup/email-verified", "/signup/info"],
    not_eligible: "/signup/not-eligible",
    pending_idv: "/signup/identity-verification",
    flagged: "/signup/flagged",
    manual_idv: "/signup/manual-identity-verification",
    duplicate_account: "/signup/duplicate-account",
    credit_freeze: "/signup/credit-freeze",
    fraud_alert: "/signup/fraud-alert",
    persona_manual_upload:
      "/signup/manual-identity-verification/persona-upload",
    pending_itin_review: "/signup/itin-submitted",
    ecbsv_consent: "/signup/ecbsv-consent",
  } satisfies Omit<
    Record<Stage["name"], string | string[]>,
    | "pending_manual_idv_review"
    | "failed_manual_idv"
    | "manual_idv_needs_reupload"
    | "passed"
  >;

  export namespace Step {
    export type Name = keyof ByName;
    export type ByName = Pick<
      User.Pii,
      "name" | "phone" | "birthDate" | "address" | "nationalId"
    >;
  }
  export type State = typeof initialState;
  export namespace State {
    export type StepByName = {
      [Step in keyof Onboarding.Step.ByName]: {
        name: Onboarding.Step.Name;
        complete: boolean;
        data: Onboarding.Step.ByName[Step];
      };
    };
  }
}

const initialState = {
  currentStep: 0,
  continuedFromIntro: false,
  stage: null as null | Onboarding.Stage,
  completedAt: null as null | Date,
};

export const { Provider, useOnboarding, Context } = FeatureStore.init(
  "Onboarding",
  initialState,
  ([store, setStore], { attachAction, wrapActions }) => {
    const [user, setUser] = useUser();
    const [, setExperiments] = useExperiments();

    const [piiEntry, setPiiEntry] = [
      () => (store.stage?.name === "pii_entry" ? store.stage : undefined),
      ((...args: any[]) => {
        if (store.stage?.name !== "pii_entry")
          throw new Error(
            "Trying to set Onboarding `pii_entry` stage state when stage is not `pii_entry`."
          );
        // @ts-expect-error Unable to narrow setStore from store constraint above
        setStore("stage", ...args);
      }) as SetStoreFunction<
        Extract<Onboarding.State["stage"], { name: "pii_entry" }>
      >,
    ] as const;

    const accessors = {
      stage: attachAction(
        () => store.stage?.name,
        () => actions.fetch()
      ),

      routes: attachAction(
        () => {
          if (!store.stage?.name) return;
          const routes = Onboarding.routesByStage[store.stage.name];
          if (!routes) return;
          return Array.isArray(routes) ? routes : [routes];
        },
        () => actions.fetch()
      ),
      currentStep: () => store.currentStep,
      firstIncompleteStep: () => {
        const index = piiEntry()?.stepOrder?.findIndex(
          (name) => !piiEntry()!.stepByName[name]!.complete
        );
        return index === -1 ? piiEntry()?.stepOrder?.length : index;
      },
      steps: attachAction(
        () =>
          piiEntry()?.stepOrder?.map((name) => piiEntry()!.stepByName[name]),
        () => actions.fetch(),
        () => () => store.stage
      ),
      retryReason: () => piiEntry()?.retryReason,
      step: <StepName extends Onboarding.Step.Name>(
        stepName: StepName
      ): Onboarding.State.StepByName[StepName] | undefined =>
        piiEntry()?.stepByName[stepName],

      ineligibleReason: () =>
        store.stage?.name === "not_eligible" ? store.stage.reason : undefined,

      manualIdv: {
        photoId: () =>
          store.stage?.name === "manual_idv" ? store.stage.photoId : undefined,
        selfie: () =>
          store.stage?.name === "manual_idv" ? store.stage.selfie : undefined,
      },

      completedAt: () => store.completedAt,
    };

    const us = (() => {
      type UsReqByName = {
        name: ["name", UpdateNameRequest];
        phone: ["phone", UpdatePhoneRequest];
        dob: ["birthDate", UpdateDOBRequest];
        address: ["address", UpdateAddressRequest];
        ssn: ["nationalId", UpdateSSNRequest];
      };

      const mutations = {
        status(status: OnboardingStatusPb) {
          const step = {
            name: {
              normal: "name",
              fromProto: FullNameUsa.normalize,
            },
            phone: {
              normal: "phone",
              fromProto: (req) => req.phone,
            },
            dob: {
              normal: "birthDate",
              fromProto: (req) =>
                req.dob &&
                UDate.format(UDate.parse(req.dob, "yyyymmdd"), "yyyy-mm-dd"),
            },
            address: {
              normal: "address",
              fromProto: AddressUsa.normalize,
            },
            ssn: {
              normal: "nationalId",
              fromProto: (req) => req.ssn,
            },
          } satisfies {
            [StepName in keyof UsReqByName]: UsReqByName[StepName] extends infer ReqStep extends
              [Onboarding.Step.Name, any]
              ? {
                  normal: UsReqByName[StepName][0];
                  fromProto(
                    req: MessageFrom<ReqStep[1]>
                  ): Onboarding.Step.ByName[ReqStep[0]];
                }
              : never;
          };
          const retryReasonByCompletionStatus = {
            RISK_RETRY: "default",
            ADDRESS_ISSUE: "address",
          } satisfies Record<
            (typeof retryStatuses)[number],
            Extract<Onboarding.Stage, { name: "pii_entry" }>["retryReason"]
          >;

          const stepMap = status.steps;

          batch(() => {
            setStore(
              "completedAt",
              Protobuf.Timestamp.toDate(status.completedAt) || null
            );

            const completionStatus =
              OnboardingStatusPb.CompletionStatus[status.completionStatus];
            const isRetry = UArray.includesLiteral(
              retryStatuses,
              completionStatus
            );

            if (
              status.completionStatus ===
                OnboardingStatusPb.CompletionStatus.UNKNOWN ||
              isRetry
            ) {
              if (!user.pii()?.emailConfirmed) {
                return setStore("stage", { name: "confirm_email" });
              }

              setStore("stage", { name: "pii_entry" });
              setPiiEntry(
                "stepByName",
                reconcile(
                  Object.fromEntries(
                    status.steps.map(({ name, complete, value: { value } }) => {
                      const { normal, fromProto } =
                        step[name as keyof UsReqByName];
                      return [
                        normal,
                        {
                          name: normal,
                          complete,
                          data: fromProto(value as any),
                        },
                      ];
                    })
                  ) as Onboarding.State.StepByName
                )
              );
              setPiiEntry(
                "stepOrder",
                stepMap.map(
                  ({ name }) => step[name as keyof UsReqByName].normal
                )
              );
              setPiiEntry(
                "retryReason",
                isRetry
                  ? retryReasonByCompletionStatus[completionStatus]
                  : undefined
              );
            } else mutations.completionStatus(status.completionStatus);
          });
        },
        completionStatus(status: OnboardingStatusPb.CompletionStatus) {
          const { CompletionStatus } = OnboardingStatusPb;

          switch (status) {
            case CompletionStatus.UNKNOWN:
            case CompletionStatus.ADDRESS_ISSUE:
            case CompletionStatus.RISK_RETRY:
              throw new UError.Unreachable();

            case CompletionStatus.PROGRAMS_FETCHING:
            case CompletionStatus.RISK_CHECKING:
              setStore("stage", { name: "pending_idv" });
              break;

            case CompletionStatus.DUPLICATE_ACCOUNT:
              setStore("stage", { name: "duplicate_account" });
              break;
            case CompletionStatus.FROZEN_FILE:
              setStore("stage", { name: "credit_freeze" });
              break;
            case CompletionStatus.ELIGIBLE_FOR_ECBSV:
              setStore("stage", { name: "ecbsv_consent" });
              break;
            case CompletionStatus.FRAUD_ALERT:
              setStore("stage", { name: "fraud_alert" });
              break;
            case CompletionStatus.ELIGIBLE_FOR_MANUAL_UPLOAD:
              setStore("stage", { name: "persona_manual_upload" });
              break;
            case CompletionStatus.ITIN_SUBMITTED:
              setStore("stage", { name: "pending_itin_review" });
              break;

            case CompletionStatus.WAITLIST_UNDERAGE:
              setStore("stage", {
                name: "not_eligible",
                reason: "underaged",
              });
              break;
            case CompletionStatus.WAITLIST_STATE:
              setStore("stage", {
                name: "not_eligible",
                reason: "address_subdivision",
              });
              break;
            case CompletionStatus.RISK_FLAGGED:
              setStore("stage", { name: "flagged" });
              break;

            case CompletionStatus.PROGRAMS_FETCHED:
            case CompletionStatus.RISK_PASSED:
              setStore("stage", { name: "passed" });
              break;
            default:
              // Exhaustiveness check
              throw new UError.Unreachable(
                `Unhandled completion status: ${status satisfies never}`
              );
          }
        },
      };
      const actions = {
        state: mutations.status,
        fetch() {
          setExperiments.fetch("showIdvValuePropositionPage");
          return usRpc.Onboarding.getOnboardingStatus({}).then(({ status }) => {
            mutations.status(status!);
          });
        },
        updateName(name: FullName) {
          return usRpc.Onboarding.updateName(FullNameUsa.denormalize(name))
            .SUCCESS(({ onboardingStatus }) => {
              mutations.status(onboardingStatus!);
            })
            .elseThrow(t`Failed to save name.`);
        },
        updateBirthDate(dob: Date) {
          return usRpc.Onboarding.updateDOB({
            dob: UDate.format(dob, "yyyymmdd"),
          })
            .SUCCESS(({ onboardingStatus }) => {
              mutations.status(onboardingStatus!);
            })
            .elseThrow(t`Failed to save date of birth.`);
        },
        async updatePhone(phone: string, acceptedMarketingTerms: boolean) {
          await usRpc.Onboarding.updatePhone({ phone, acceptedMarketingTerms })
            .SUCCESS(async () => {})
            .elseThrow(t`Failed to save phone.`);

          await (async function pollValidation() {
            await UPromise.delay(1000);
            return usRpc.Onboarding.getPhoneValidation({ phone })
              .WAITING(
                // Cast to `never` to prevent cyclic type reference
                pollValidation as never
              )
              .VALID(() => {})
              .INVALID(({ error }) => {
                throw new Error(
                  error?.description ||
                    "Phone number invalid. Only non-VOIP mobile phone numbers are accepted."
                );
              })
              .elseThrow(t`Failed to validate phone number.`);
          })();

          return usRpc.Onboarding.getPhoneCode({ phone })
            .SUCCESS(() => {
              setPiiEntry("phoneConfirmationToken", phone);
            })
            .ALREADY_CONFIRMED(() => "ALREADY_CONFIRMED" as const)
            .elseThrow(t`Failed to get phone code.`);
        },
        resendPhoneCode(phone: string) {
          return usRpc.Onboarding.getPhoneCode({ phone })
            .SUCCESS(() => {
              setPiiEntry("phoneConfirmationToken", phone);
            })
            .elseThrow(t`Failed to resend phone code.`);
        },
        confirmPhone(code: string) {
          return usRpc.Onboarding.confirmPhone({
            code,
            phone: piiEntry()!.phoneConfirmationToken,
          })
            .SUCCESS(({ onboardingStatus }) => {
              mutations.status(onboardingStatus!);
            })
            .elseThrow(t`Failed to confirm phone.`);
        },
        updateAddress(address: Address) {
          return usRpc.Onboarding.updateAddress(AddressUsa.denormalize(address))
            .SUCCESS(({ onboardingStatus }) => {
              mutations.status(onboardingStatus!);
            })
            .elseThrow(t`Failed to save address.`);
        },
        updateNationalId(nationalId: string) {
          return usRpc.Onboarding.updateSSN({ ssn: nationalId })
            .SUCCESS(({ onboardingStatus }) => {
              mutations.status(onboardingStatus!);
            })
            .elseThrow(t`Failed to save SSN.`);
        },
        completeForm() {
          return usRpc.Onboarding.completeForm({})
            .SUCCESS(() => actions.fetch())
            .elseThrow(t`Failed to complete form.`);
        },
        retryPii() {
          return usRpc.Onboarding.retryPii({})
            .SUCCESS(({ onboardingStatus }) => {
              mutations.status(onboardingStatus!);
            })
            .elseThrow(t`Failed to initiate retry.`);
        },
        verifyEcbsv(consentGiven: boolean) {
          return usRpc.Onboarding.runEcbsvCheck({ consentGiven: consentGiven })
            .SUCCESS(({ onboardingStatus }) => {
              mutations.status(onboardingStatus!);
            })
            .elseThrow(t`Failed to start verification.`);
        },
        startManualIdv: UFunction.todo,
        uploadIdvDocuments: UFunction.todo,
        restartIdv: UFunction.todo,
      };
      return { actions };
    })();

    const canada = (() => {
      const stepOrder = [
        "name",
        "phone",
        "birthDate",
        "address",
      ] satisfies Onboarding.Step.Name[];
      const stepFromProto = {
        name: (proto) =>
          proto?.fullName && FullNameCanada.normalize(proto.fullName),
        phone: (proto) => proto?.phone,
        birthDate: (proto) => proto?.birthDate,
        address: (proto) => proto?.address,
      } satisfies {
        [Name in (typeof stepOrder)[number]]: (
          proto: OnboardingStatePb[`${Name}Step`]
        ) => Onboarding.Step.ByName[Name] | undefined;
      };

      const mutations = {
        state(state: OnboardingStatePb) {
          batch(() => {
            setStore(
              "completedAt",
              Protobuf.Timestamp.toDate(state.completedAt) || null
            );

            const isRetry =
              state.completionStatus ===
              OnboardingStatePb.CompletionStatus.RISK_RETRY;

            if (
              state.completionStatus ===
                OnboardingStatePb.CompletionStatus.UNKNOWN ||
              isRetry
            ) {
              if (!user.pii()?.emailConfirmed) {
                return setStore("stage", { name: "confirm_email" });
              }

              setStore("stage", { name: "pii_entry" });
              setPiiEntry(
                "stepByName",
                reconcile(
                  Object.fromEntries(
                    stepOrder.map((name) => [
                      name,
                      {
                        name,
                        complete: state[`${name}Step`]!.completed,
                        // @ts-expect-error TS unable to link [name] and state[`${name}Step`]
                        data: stepFromProto[name](state[`${name}Step`]),
                      },
                    ])
                  ) as Onboarding.State.StepByName
                )
              );
              setPiiEntry("stepOrder", stepOrder);
              setPiiEntry("retryReason", isRetry ? "default" : undefined);
            } else mutations.completionStatus(state.completionStatus);
          });
        },
        completionStatus(status: OnboardingStatePb.CompletionStatus) {
          const { CompletionStatus } = OnboardingStatePb;

          switch (status) {
            case CompletionStatus.INELIGIBLE_SUBDIVISION:
              setStore("stage", {
                name: "not_eligible",
                reason: "address_subdivision",
              });
              break;
            case CompletionStatus.RISK_CHECKING:
              setStore("stage", {
                name: "pending_idv",
              });
              break;
            case CompletionStatus.RISK_FLAGGED:
              setStore("stage", {
                name: "flagged",
              });
              break;
            case CompletionStatus.MANUAL_IDV_NEEDED:
              setStore("stage", {
                name: "manual_idv",
              });
              break;
            case CompletionStatus.MANUAL_IDV_NEEDS_REVIEW:
              setStore("stage", {
                name: "pending_manual_idv_review",
              });
              break;
            case CompletionStatus.MANUAL_IDV_NEEDS_REUPLOAD:
              setStore("stage", {
                name: "manual_idv_needs_reupload",
              });
              break;
            case CompletionStatus.MANUAL_IDV_FAILED:
              setStore("stage", {
                name: "failed_manual_idv",
              });
              break;
            case CompletionStatus.RISK_PASSED:
              setStore("stage", {
                name: "passed",
              });
              break;
          }
        },
      };

      const actions = {
        state: mutations.state,
        fetch() {
          return canadaRpc.Onboarding.showState({}).then(
            ({ onboardingState }) => {
              mutations.state(onboardingState!);
            }
          );
        },
        updateName(name: FullName) {
          return canadaRpc.Onboarding.updateName({
            fullName: FullNameCanada.denormalize(name),
          }).then(({ onboardingState }) => {
            mutations.state(onboardingState!);
          });
        },
        updatePhone(phone: string) {
          return canadaRpc.Onboarding.updatePhone({
            phone,
          }).then(({ confirmationToken }) => {
            setPiiEntry("phoneConfirmationToken", confirmationToken);
          });
        },
        resendPhoneCode: UFunction.todo,
        confirmPhone(code: string) {
          return canadaRpc.Onboarding.confirmPhone({
            code,
            confirmationToken: piiEntry()!.phoneConfirmationToken,
          }).then(({ onboardingState }) => {
            mutations.state(onboardingState!);
          });
        },
        updateBirthDate(birthDate: Date) {
          return canadaRpc.Onboarding.updateBirthDate({
            birthDate: UDate.format(birthDate, "yyyymmdd"),
          }).then(({ onboardingState }) => {
            mutations.state(onboardingState!);
          });
        },
        updateAddress(address: Address) {
          return canadaRpc.Onboarding.updateAddress({
            address,
          }).then(({ onboardingState }) => {
            mutations.state(onboardingState!);
          });
        },
        updateNationalId: UError.Unsupported.fn,
        completeForm() {
          return canadaRpc.Onboarding.completeForm({}).then(
            ({ onboardingState }) => {
              mutations.state(onboardingState!);
            }
          );
        },
        verifyEcbsv: UError.Unsupported.fn,
        startManualIdv() {
          return canadaRpc.Onboarding.startManualIdv({}).then(
            async ({ onboardingState }) => {
              mutations.state(onboardingState!);
            }
          );
        },
        async uploadIdvDocuments(photoId: File, selfie: File) {
          return (
            canadaRpc.Onboarding.submitIdvDocuments({
              documentImage: new Uint8Array(await photoId.arrayBuffer()),
              selfieImage: new Uint8Array(await selfie.arrayBuffer()),
            })
              /**
               * NOTE: we do not update/fetch the onboardingState here since
               * this action should be only called in one place in one flow,
               * where the next page fetches the updated onboardingState.
               *
               * See:
               * .../signup/manual-identity-verification/upload-selfie
               * .../signup/manual-identity-verification/upload-successful
               */
              .then(({}) => {})
          );
        },
        restartIdv() {
          return canadaRpc.Onboarding.restartIdv({}).then(
            async ({ onboardingState }) => {
              mutations.state(onboardingState!);
            }
          );
        },
        retryPii: () => Promise.resolve(),
      };

      return { actions };
    })();

    const actions = wrapActions({
      currentStep(step: number) {
        if (step < 0 || step > (piiEntry()?.stepOrder.length || -1)) return;
        setStore("currentStep", step);
      },
      pollIdv({
        // `onError` instead of native promise `catch` because we want to both
        // show the error message and keep polling
        onError = UFunction.noop as (error: Error) => void,
      } = {}) {
        let cancelled = false;
        (async function poll() {
          if (cancelled) return;

          await actions.fetch().catch((e) => {
            onError(e);
          });
          if (store.stage?.name === "pending_idv") {
            await UPromise.delay(2000);
            await poll();
          } else await setUser.fetch();
        })().catch((e) => {
          onError(e);
        });
        return {
          cancel() {
            cancelled = true;
          },
        };
      },
      manualIdv: {
        photoId(file: File | undefined) {
          // @ts-expect-error
          setStore("stage", "photoId", file);
        },
        selfie(file: File | undefined) {
          // @ts-expect-error
          setStore("stage", "selfie", file);
        },
      },
      ...(import.meta.env.API_ADAPTER === "canada"
        ? canada.actions
        : us.actions),
    });

    return { accessors, actions };
  }
);
